import { GeoJsonUtils } from '../GeoJsonUtils/GeoJsonUtils';

export class PosUtils {
  /**
   * Calculates the bounding box for an array of arrays where the sub-array contains
   * indices for latitude and longitude
   *
   * @param {*} positions
   * @param {*} lonIdx
   * @param {*} latIdx
   */
  static getBbox (positions, lonIdx, latIdx) {
    var bbox = [null, null, null, null];

    if (positions.length > 0) {
      // Calculate bounding box
      positions.forEach(pos => {
        if (bbox[0] === null || bbox[0] > pos[lonIdx]) {
          bbox[0] = pos[lonIdx];
        }
        if (bbox[2] === null || bbox[2] < pos[lonIdx]) {
          bbox[2] = pos[lonIdx];
        }
        if (bbox[1] === null || bbox[1] > pos[latIdx]) {
          bbox[1] = pos[latIdx];
        }
        if (bbox[3] === null || bbox[3] < pos[latIdx]) {
          bbox[3] = pos[latIdx];
        }
      });
    }
    return bbox;
  }

  /**
   * Converts from degrees to radians
   *
   * @param {*} degrees
   */
  static toRadians (degrees) {
    return (degrees * Math.PI) / 180;
  }

  /**
   * Converts from radians to degrees
   *
   * @param {*} radians
   */
  static toDegrees (radians) {
    return (radians * 180) / Math.PI;
  }

  /**
   * Calculates bearing from two sets of latitude/longitude pairs
   */
  static getBearing (startLat, startLng, destLat, destLng) {
    startLat = PosUtils.toRadians(startLat);
    startLng = PosUtils.toRadians(startLng);
    destLat = PosUtils.toRadians(destLat);
    destLng = PosUtils.toRadians(destLng);

    var y = Math.sin(destLng - startLng) * Math.cos(destLat);
    var x =
      Math.cos(startLat) * Math.sin(destLat) -
      Math.sin(startLat) * Math.cos(destLat) * Math.cos(destLng - startLng);
    var brng = Math.atan2(y, x);
    brng = PosUtils.toDegrees(brng);
    return (brng + 360) % 360;
  }

  /**
   * Get accumulated positions a specific time offset in seconds
   *
   * @param {*} timeSeries
   * @param {*} start
   * @param {*} secondsFromStart
   */
  static getPositionsForOffset (timeSeries, start, secondsFromStart) {
    var currentTime;

    if (secondsFromStart === 0) {
      currentTime = new Date(start.getTime());
    } else {
      currentTime = new Date(start.getTime() + secondsFromStart * 1000);
    }

    return PosUtils.getPositionsUpToTime(timeSeries, currentTime);
  }

  /**
   * Get accumulated positions up to a specific time
   *
   * @param {*} timeSeries
   * @param {*} currentTime
   */
  static getPositionsUpToTime (timeSeries, currentTime) {
    // Array to hold last position
    var positions = [];

    // Array to hold accumulated track
    var tracks = [];

    // Loop through time series
    timeSeries.forEach((timeSerie, mmsi) => {
      var lineStringCoords = [];
      for (var i = 1; i < timeSerie.positions.length; i++) {
        var firstPos = timeSerie.positions[i - 1];
        var secondPos = timeSerie.positions[i];

        if (currentTime >= firstPos[1]) {
          lineStringCoords.push([firstPos[2], firstPos[3]]);
        }

        if (currentTime >= firstPos[1] && currentTime < secondPos[1]) {
          var timeBetweenPos = secondPos[1] - firstPos[1];
          var timeSinceFirstPos = currentTime - firstPos[1];
          var fraction = timeSinceFirstPos / timeBetweenPos;
          var deltaLon = secondPos[2] - firstPos[2];
          var deltaLat = secondPos[3] - firstPos[3];
          var newLon = firstPos[2] + deltaLon * fraction;
          var newLat = firstPos[3] + deltaLat * fraction;
          var bearing = PosUtils.getBearing(firstPos[3], firstPos[2], secondPos[3], secondPos[2]);

          positions.push({
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: [newLon, newLat]
            },
            properties: {
              mmsi: firstPos[0],
              cog: firstPos[4],
              bearing: bearing
            }
          });

          lineStringCoords.push([newLon, newLat]);
          break;
        }
      }
      if (lineStringCoords.length > 1) {
        tracks.push({
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: lineStringCoords
          },
          properties: {
            mmsi: firstPos[0]
          }
        });
      }
    });

    return {
      positions: {
        type: 'FeatureCollection',
        features: positions.slice()
      },
      tracks: {
        type: 'FeatureCollection',
        features: tracks.slice()
      }
    };
  }

  static posDistance (pos1, pos2) {
    return Math.sqrt(
      Math.pow(Math.abs(pos2[2] - pos1[2]), 2) + Math.pow(Math.abs(pos2[3] - pos1[3]), 2)
    );
  }

  static sameMmsi (pos1, pos2) {
    return pos1 && pos2 && pos1[0] === pos2[0];
  }

  static laterDate (pos1, pos2) {
    return pos1 && pos2 && pos1[1] > pos2[1];
  }

  static sameLocation (pos1, pos2) {
    return pos1 && pos2 && pos1[2] !== pos2[2] && pos1[3] !== pos2[3];
  }

  static minApart (pos1, pos2) {
    return Math.abs(pos1[1] - pos2[1]) / 1000 / 60;
  }

  static lastPosition (positionIndex, numPositions) {
    return positionIndex === numPositions - 1;
  }

  /**
   * Create an array of time series for each unique ship
   *
   * @param {*} positions Array of position messages
   * @param {*} statinfo Array of static messages
   */
  static createTimeSeries (positions, statinfo) {
    // An array with position series for each ship
    var timeSeriesCollection = [];

    // Create temporary position object
    var tmpPosObj = {};

    // Create an array for each mmsi
    positions.forEach(p => {
      if (tmpPosObj[p[0]] === undefined) {
        tmpPosObj[p[0]] = [];
      }
      p[1] = new Date(p[1]);
      tmpPosObj[p[0]].push(p.slice());
    });

    // Populate each mssi position array
    for (var p in tmpPosObj) {
      timeSeriesCollection.push({
        mmsi: +p,
        positions: tmpPosObj[p].slice(0)
      });
    }

    // Clean position arrays based on position spacing
    // var dismissed = 0;
    timeSeriesCollection.forEach(ts => {
      var rawPos = ts.positions;
      var cleanPos = [];
      rawPos.forEach((p, i, a) => {
        if (i > 0) {
          var pos1 = a[i - 1];
          var pos2 = a[i];
          if (this.posDistance(pos1, pos2) <= 0.01 && this.posDistance(pos1, pos2) >= 0.00005) {
            cleanPos.push(p);
          }
        } else {
          cleanPos.push(p);
        }
      });
      ts.positions = cleanPos.slice(0);
    });

    // Split position arrays where too long time between observations
    let splitTracks = [];
    timeSeriesCollection.forEach(shipTrack => {
      var sPos = shipTrack.positions;
      if (sPos.length >= 2) {
        var tmpPos = [];
        for (var pIdx = 1; pIdx < sPos.length; pIdx++) {
          // Get first pos
          var pos1 = sPos[pIdx - 1];

          // Get second pos
          var pos2 = sPos[pIdx];

          // If first iteration, add pos1 to array
          if (pIdx === 1 || tmpPos.length === 0) {
            tmpPos.push(pos1);
          } else if (pIdx === sPos.length - 1) {
            tmpPos.push(pos2);
          }

          // Check if min apart
          if (this.minApart(pos1, pos2) > 15 || pIdx === sPos.length - 1) {
            let splitTrack = {
              ...shipTrack,
              positions: []
            };
            splitTrack.positions = tmpPos.slice(0);
            splitTracks.push(splitTrack);
            tmpPos.length = 0;
          } else {
            tmpPos.push(pos2);
          }
        }
      }
    });
    timeSeriesCollection = splitTracks;

    // Add ship info to series
    if (statinfo !== undefined && Array.isArray(statinfo) && statinfo.length > 0) {
      timeSeriesCollection.forEach((timeSeries, idx, arr) => {
        for (var si = 0; si < statinfo.length; si++) {
          var currentStatinfo = statinfo[si];
          if (currentStatinfo.mmsi === timeSeries.mmsi) {
            arr[idx] = Object.assign(timeSeries, currentStatinfo);
            break;
          }
        }
      });
    }
    return timeSeriesCollection;
  }

  /**
   * Create GeoJSON features from array of positions
   * @param {*} positions
   */
  static createFeaturesFromPositions (positions) {
    var f = {
      features: [],
      mmsiIds: []
    };

    var trackIndex = 0;

    if (positions.length >= 2) {
      for (var i = 1; i < positions.length - 1; i++) {
        // Select position and position -1
        var p1 = positions[i - 1];
        if (i === 1) {
          f.mmsiIds.push(p1[0]);
        }
        var p2 = positions[i];

        if (p2 === undefined) {
          // For some reason, a position vas zero
          continue;
        }

        // If the latitude or longitude is the same, replace previous point
        try {
          while (p2[2] === p1[2] || p2[3] === p1[3]) {
            i++;
            p2 = positions[i];
          }
        } catch (error) {
          continue;
        }

        // If they have the same MMSI id, consider them a line
        if (p1[0] === p2[0]) {
          var timeDiff = p2[1] - p1[1];
          var posDiffX = p2[2] - p1[2];
          var posDiffY = p2[3] - p1[3];

          // Exclude lines where there is more than 10 minutes between position messages and
          // distance between two points is less than one 10th of a degree.
          if (timeDiff < 60000 && (posDiffY < 0.1 || posDiffX < 0.1)) {
            // Build GeoJSON features
            var feature = GeoJsonUtils.feature();
            feature.geometry = GeoJsonUtils.lineString([[p1[2], p1[3]], [p2[2], p2[3]]]);
            feature.properties.trackIndex = trackIndex;
            feature.properties.id = i;
            feature.properties.mmsi = p1[0];
            feature.properties.start = p1.timeFormat;
            feature.properties.end = p2.timeFormat;
            feature.properties.endt = p2[1];
            feature.properties.speed = p2[5];
            feature.properties.msg = p2[6];
            f.features.push(feature);
          } else {
            // Increment track index if a segment is skipped
            trackIndex++;
          }
        } else {
          // Increment track index for each unique mmsi
          trackIndex++;
          f.mmsiIds.push(p1[0]);
        }
      }
    }
    return f;
  }
}
